/**
 * DivisionRail — the CSI Divisions rail shared by Master Specs and Add Specs.
 * "All sections" row + one row per division (code · name · count). Master Specs
 * adds expand-to-specs through `renderExpanded`; `footer` hosts its pending
 * reviews. `compact` collapses rows to code + count (never hidden).
 */
import React from 'react';
import { rem } from '../lib/rem';

export interface DivisionRailItem {
  code: string;
  name: string;
  count: number;
}

interface DivisionRailProps {
  divisions: DivisionRailItem[];
  total: number;
  selected: string | null;
  /** next = new selection (null = All / deselect); clicked = the division row clicked, if any. */
  onSelect: (next: string | null, clicked?: string) => void;
  /** Codes-only rows (narrow windows). */
  compact?: boolean;
  /** Expand-to-specs (Master Specs only): which divisions are open + their content. */
  expanded?: Record<string, boolean>;
  renderExpanded?: (code: string) => React.ReactNode;
  /** Pinned below the list (Master Specs pending reviews). */
  footer?: React.ReactNode;
}

const RAIL_WIDTH = rem(240);
const RAIL_WIDTH_COMPACT = rem(104);

export function DivisionRail({
  divisions,
  total,
  selected,
  onSelect,
  compact = false,
  expanded,
  renderExpanded,
  footer,
}: DivisionRailProps) {
  const width = compact ? RAIL_WIDTH_COMPACT : RAIL_WIDTH;
  return (
    <div
      style={{
        width,
        minWidth: width,
        borderRight: '1px solid var(--bd)',
        background: 'var(--sf)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: 'var(--space-3) var(--space-3) var(--space-2)',
          borderBottom: '1px solid var(--bd)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: 'var(--tm)',
            textTransform: 'uppercase',
            letterSpacing: '.05em',
          }}
        >
          {compact ? 'Div.' : 'CSI Divisions'}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-1) 0' }}>
        <div
          onClick={() => onSelect(null)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-base)',
            fontWeight: !selected ? 600 : 400,
            background: !selected ? 'rgba(var(--acr),0.1)' : 'transparent',
            color: !selected ? 'var(--ac)' : 'var(--tx)',
            borderRadius: 'var(--radius-sm)',
            margin: '0 var(--space-2)',
            transition: 'all var(--duration-fast) var(--ease-out)',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-base)' }}>All{compact ? '' : ' sections'}</span>
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 'var(--font-size-xs)',
              padding: '1px var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--sa)',
              color: 'var(--tm)',
            }}
          >
            {total}
          </span>
        </div>
        {divisions.map((d) => {
          const isExp = !!expanded?.[d.code];
          const isSel = selected === d.code;
          return (
            <div key={d.code}>
              <div
                onClick={() => onSelect(isSel ? null : d.code, d.code)}
                title={compact ? `${d.code} · ${d.name}` : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) var(--space-3)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: isSel ? 600 : 400,
                  background: isSel ? 'rgba(var(--acr),0.1)' : 'transparent',
                  color: isSel ? 'var(--ac)' : 'var(--tx)',
                  borderRadius: 'var(--radius-sm)',
                  margin: '1px var(--space-2)',
                  transition: 'all var(--duration-fast) var(--ease-out)',
                }}
              >
                {renderExpanded && (
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-flex',
                      color: 'var(--tm)',
                      transform: isExp ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform var(--duration-fast) var(--ease-out)',
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width={12}
                      height={12}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </span>
                )}
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--ac)',
                    opacity: 0.7,
                    flexShrink: 0,
                  }}
                >
                  {d.code}
                </span>
                {!compact && (
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {d.name}
                  </span>
                )}
                <span
                  style={{
                    marginLeft: compact ? 'auto' : undefined,
                    fontSize: 'var(--font-size-xs)',
                    padding: '1px var(--space-1)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--sa)',
                    color: 'var(--tm)',
                    flexShrink: 0,
                  }}
                >
                  {d.count}
                </span>
              </div>
              {isExp && renderExpanded && <div>{renderExpanded(d.code)}</div>}
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
}
